import React, { createContext, forwardRef, useContext, useId, useMemo } from "react";
import { Global, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import * as RechartsPrimitive from "recharts";

// File adapted from shadcn https://ui.shadcn.com/docs/components/chart
// Adapated to use styled-components instead of tailwindcss

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    color?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tooltipFormatter?: (value: any) => React.ReactNode; // Rechart uses any for data, can't really be more specific
  };
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = createContext<ChartContextProps | null>(null);

function useChart() {
  const context = useContext(ChartContext);

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }

  return context;
}

const ChartContainer = forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig;
    children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>["children"];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <ChartContainerStyled data-chart={chartId} ref={ref} className={className} {...props}>
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
      </ChartContainerStyled>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = "Chart";

const ChartContainerStyled = styled.div(({ theme }) => ({
  display: "flex",
  aspectRatio: "16 / 9",
  justifyContent: "center",
  fontSize: "0.75rem",
  lineHeight: "1rem",

  "& .recharts-cartesian-axis-tick_text": {
    color: theme.colors.sysTextPlaceholder, // fill-muted-foreground
  },
  "& .recharts-cartesian-grid_line[stroke='#ccc']": {
    stroke: `${theme.colors.sysBorderPrimary}80`, // stroke-border/50 (80 = 50% opacity in hex)
  },
  "& .recharts-curve.recharts-tooltip-cursor": {
    stroke: theme.colors.sysBorderPrimary, // stroke-border
  },
  "& .recharts-dot[stroke='#fff']": {
    stroke: "transparent",
  },
  "& .recharts-layer": {
    outline: "2px solid transparent",
    outlineOffset: "2px",
  },
  "& .recharts-polar-grid [stroke='#ccc']": {
    stroke: theme.colors.sysBorderPrimary, // stroke-border
  },
  "& .recharts-radial-bar-background-sector": {
    fill: theme.colors.sysBackgroundAlternative, // fill-muted
  },
  "& .recharts-rectangle.recharts-tooltip-cursor": {
    fill: theme.colors.sysBackgroundAlternative, // fill-muted
  },
  "& .recharts-reference-line [stroke='#ccc']": {
    stroke: theme.colors.sysBorderPrimary, // stroke-border
  },
  "& .recharts-sector[stroke='#fff']": {
    stroke: "transparent",
  },
  "& .recharts-sector": {
    outline: "2px solid transparent",
    outlineOffset: "2px",
  },
  "& .recharts-surface": {
    outline: "2px solid transparent",
    outlineOffset: "2px",
  },
}));

/**
 * Create a CSS variable for each item within the chart config
 * This is so within the chart you don't need to get the color from the theme
 * and pass it down. Instead you use that CSS variable which is the item key
 *
 * e.g. foo: { label: "Unrelated", color: "red" } -> var(--color-foo)
 */
const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const theme = useTheme();

  const colorConfig = Object.entries(config).map(([key, itemConfig], i) => ({
    key,
    color: itemConfig.color || `sysChart${i + 1}`,
  }));

  return (
    <Global
      styles={{
        [`[data-chart=${id}]`]: Object.assign(
          {},
          ...colorConfig.map(({ key, color }) => ({
            [`--color-${key}`]: Object.keys(theme.colors).includes(color as string)
              ? theme.colors[color as keyof typeof theme.colors]
              : color,
          })),
        ),
      }}
    />
  );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

type TooltipIndicator = "line" | "dot" | "dashed";

const ChartTooltipContent = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<"div"> & {
      hideLabel?: boolean;
      hideIndicator?: boolean;
      indicator?: TooltipIndicator;
      nameKey?: string;
      labelKey?: string;
      extraRows?: string[]; // Extra rows to show in the tooltip
    }
>(
  (
    {
      active,
      payload: payloadOriginal,
      className,
      indicator = "dot",
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey,
      extraRows,
    },
    ref,
  ) => {
    const { config } = useChart();

    const payload = useMemo(() => {
      if (!extraRows?.length || !payloadOriginal?.length) {
        return payloadOriginal;
      }

      const payloadExtraRows = extraRows.map((row) => ({
        ...payloadOriginal[0],
        dataKey: row,
        name: row,
        value: payloadOriginal[0].payload[row],
        fill: `var(--color-${row})`,
        color: `var(--color-${row})`,
      }));

      return [...payloadOriginal, ...payloadExtraRows];
    }, [payloadOriginal, extraRows]);

    const tooltipLabel = useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null;
      }

      const [item] = payload;
      const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      const value =
        !labelKey && typeof label === "string"
          ? config[label as keyof typeof config]?.label || label
          : itemConfig?.label;

      if (labelFormatter) {
        return (
          <ChartTooltipLabelNested className={labelClassName}>
            {labelFormatter(value, payload)}
          </ChartTooltipLabelNested>
        );
      }

      if (!value) {
        return null;
      }

      return <ChartTooltipLabelNested>{value}</ChartTooltipLabelNested>;
    }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

    if (!active || !payload?.length) {
      return null;
    }

    const nestLabel = payload.length === 1 && indicator !== "dot";

    return (
      <ChartTooltipWrapper ref={ref} className={className}>
        {!nestLabel ? tooltipLabel : null}
        <ChartTooltipWrapperInner>
          {payload.map((item, index) => {
            const key = `${nameKey || item.name || item.dataKey || "value"}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = color || item.payload.fill || item.color;

            return (
              <ChartTooltipElement key={item.dataKey} indicator={indicator}>
                {
                  // Use the formatter or fallback to the default rendering
                  formatter && item?.value !== undefined && item.name ? (
                    formatter(item.value, item.name, item, index, item.payload)
                  ) : (
                    <>
                      {itemConfig?.icon ? (
                        <itemConfig.icon />
                      ) : (
                        !hideIndicator && (
                          <ChartTooltipIndicator
                            indicatorColor={indicatorColor}
                            indicator={indicator}
                            nestLabel={nestLabel}
                          />
                        )
                      )}
                      <ChartTooltipContents nestLabel={nestLabel}>
                        <ChartTooltipLabelContainer>
                          {nestLabel ? tooltipLabel : null}
                          <ChartTooltipLabelUnnested>
                            {itemConfig?.label || item.name}
                          </ChartTooltipLabelUnnested>
                        </ChartTooltipLabelContainer>
                        {item.value && (
                          <ChartTooltipValue>
                            {itemConfig?.tooltipFormatter
                              ? itemConfig.tooltipFormatter(item.value)
                              : item.value.toLocaleString()}
                          </ChartTooltipValue>
                        )}
                      </ChartTooltipContents>
                    </>
                  )
                }
              </ChartTooltipElement>
            );
          })}
        </ChartTooltipWrapperInner>
      </ChartTooltipWrapper>
    );
  },
);
ChartTooltipContent.displayName = "ChartTooltip";

const ChartTooltipLabelNested = styled.div({
  fontWeight: "500",
});

const ChartTooltipWrapper = styled.div(({ theme }) => ({
  display: "grid",
  minWidth: "8rem",
  alignItems: "flex-start",
  gap: "0.375rem",
  borderRadius: "0.5rem",
  borderWidth: "1px",
  borderColor: `${theme.colors.sysBorderPrimary}80`, // stroke-border/50 (80 = 50% opacity in hex)
  backgroundColor: theme.colors.sysBackgroundDefault, // background
  padding: "0.375rem 0.625rem",
  fontSize: "0.75rem",
  lineHeight: "1rem",
  boxShadow: "0 20px 25px -5px rgba(0,0,0,.1), 0 8px 10px -6px rgba(0,0,0,.1)",
}));

const ChartTooltipWrapperInner = styled.div({
  display: "grid",
  gap: "0.375rem",
});

const ChartTooltipElement = styled.div<{ indicator: TooltipIndicator }>(({ theme, indicator }) => ({
  display: "flex",
  width: "100%",
  flexWrap: "wrap",
  gap: "0.5rem",

  ...(indicator === "dot"
    ? {
        alignItems: "center",
      }
    : {
        alignItems: "stretch",
      }),

  "& > svg": {
    height: "0.625rem",
    width: "0.625rem",
    color: theme.colors.sysTextPlaceholder, // text-muted-foreground
  },
}));

const ChartTooltipIndicator = styled.div<{
  indicatorColor: string;
  indicator: TooltipIndicator;
  nestLabel: boolean;
}>(({ indicatorColor, indicator, nestLabel }) => ({
  flexShrink: 0,
  borderRadius: "2px",
  borderColor: indicatorColor,
  backgroundColor: indicatorColor,

  ...(indicator === "dot" && {
    height: "0.625rem",
    width: "0.625rem",
  }),
  ...(indicator === "line" && {
    width: "0.25rem",
  }),
  ...(indicator === "dashed" && {
    width: "0px",
    borderWidth: "1.5px",
    borderStyle: "dashed",
    backgroundColor: "transparent",
    ...(nestLabel && {
      marginTop: "0.125rem",
      marginBottom: "0.125rem",
    }),
  }),
}));

const ChartTooltipContents = styled.div<{ nestLabel: boolean }>(({ nestLabel }) => ({
  display: "flex",
  flex: "1",
  gap: 8,
  justifyContent: "space-between",
  lineHeight: 1,
  alignItems: nestLabel ? "flex-end" : "center",
}));

const ChartTooltipLabelContainer = styled.div({
  display: "grid",
  gap: "0.375rem",
});

const ChartTooltipLabelUnnested = styled.span(({ theme }) => ({
  color: theme.colors.sysTextPlaceholder, // text-muted-foreground
}));

const ChartTooltipValue = styled.span(({ theme }) => ({
  // fontFamily: monospace font
  fontWeight: "500",
  fontVariantNumeric: "tabular-nums",
  color: theme.colors.sysTextDefault, // text-foreground
}));

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> &
    Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign" | "align"> & {
      hideIcon?: boolean;
      nameKey?: string;
    }
>(({ className, hideIcon = false, payload, verticalAlign = "bottom", align, nameKey }, ref) => {
  const { config } = useChart();

  if (!payload?.length) {
    return null;
  }

  return (
    <ChartLegendWrapper ref={ref} className={className} verticalAlign={verticalAlign} align={align}>
      {payload.map((item) => {
        const key = `${nameKey || item.dataKey || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);

        return (
          <ChartLegendElement key={item.value}>
            {itemConfig?.icon && !hideIcon ? (
              <itemConfig.icon />
            ) : (
              <ChartLegendSquareIcon color={itemConfig?.color} />
            )}
            {itemConfig?.label}
          </ChartLegendElement>
        );
      })}
    </ChartLegendWrapper>
  );
});
ChartLegendContent.displayName = "ChartLegend";

const ChartLegendWrapper = styled.div<{
  verticalAlign: RechartsPrimitive.LegendProps["verticalAlign"];
  align?: string;
}>(({ verticalAlign, align = "center" }) => ({
  display: "flex",
  alignItems: "start",
  justifyContent: align,
  gap: "1rem",
  ...(verticalAlign === "top"
    ? {
        paddingBottom: "0.75rem",
      }
    : {
        paddingTop: "0.75rem",
      }),
}));

const ChartLegendElement = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.375rem",

  "& > svg": {
    height: "0.75rem",
    width: "0.75rem",
    color: theme.colors.sysTextPlaceholder, // text-muted-foreground
  },
}));

const ChartLegendSquareIcon = styled.div<{ color?: string }>(({ color }) => ({
  height: "0.5rem",
  width: "0.5rem",
  flexShrink: 0,
  borderRadius: "2px",
  backgroundColor: color,
}));

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  const payloadPayload =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2339 - Remove after we upgrade to TypeScript 5+, line below just problem in v4
    "payload" in payload && typeof payload.payload === "object" && payload.payload !== null
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TS2339 - Remove after we upgrade to TypeScript 5+, line below just problem in v4
        payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (key in payload && typeof payload[key as keyof typeof payload] === "string") {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
  }

  return configLabelKey in config ? config[configLabelKey] : config[key as keyof typeof config];
}

export { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent };
